import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useSuiteStore } from '@/stores/useSuiteStore'

export interface H3aDetailRow {
  rdvId: number
  h3aId: string | null
  commercialId: number | null
  commercial: string
  auteur: string
  bu: string
  region: string
  equipe: string
  agence: string
  dateEmission: string | null
  dateRdv: string | null
  statut: string
  siren: string
  siret: string | null
  societe: string
  source: string | null
  nbCommandes: number
  montantTotal: number
  margeTotale: number
  margeFixe: number
  margeMobile: number
  commentaire: string | null
}

export interface H3aMonthlyRow {
  month: number
  monthLabel: string
  rdvGeneres: number | null
  rdvValides: number
  tauxQualification: number | null
  rdvAnnules: number
  tauxAnnulation: number | null
  caGenere: number | null
  margeGeneree: number
  margeMoyenneParRdvValide: number
  nbCommandes: number
  margeMoyenneParCommande: number
  objectif: number | null
  niveauAtteinte: number | null
  coutPrestation: number | null
  coutParRdvGenere: number | null
  coutParRdvValide: number | null
  roi: number | null
}

export interface H3aMonthlySetting {
  month: number
  objectif: number | null
  coutPrestation: number | null
}

export type H3aBu = 'odyc' | 'unixo' | 'unixoNegoce' | 'odycVad'
export type H3aDateField = 'createAt' | 'date'
export type H3aMatchKey = 'siret' | 'siren'
export type H3aSortBy = 'id' | 'commercial' | 'auteur' | 'bu' | 'region' | 'equipe' | 'agence' | 'statut' | 'dateRdv'

export interface H3aMonthlyLabels {
  generes: string
  valides: string
  annules: string
  margeMoyenne: string
  coutGenere: string
  coutValide: string
}

const RDV_LABELS: H3aMonthlyLabels = {
  generes: 'RDV générés',
  valides: 'RDV validés',
  annules: 'RDV annulés',
  margeMoyenne: 'Marge moyenne par RDV validé',
  coutGenere: 'Coût par RDV généré',
  coutValide: 'Coût par RDV validé',
}

interface H3aStatsResult {
  year: number
  bu: H3aBu
  commercialId: number | null
  dateField: H3aDateField
  matchKey: H3aMatchKey
  productSplit: boolean
  orderBased: boolean
  labels: H3aMonthlyLabels
  facets?: H3aFacets
  generatedAt: string
  notes: string[]
  detail: H3aDetailRow[]
  monthly: H3aMonthlyRow[]
}

export interface CommercialOption {
  id: number
  name: string
}

export interface H3aFacets {
  ids: number[]
  commercials: string[]
  auteurs: string[]
  bus: string[]
  regions: string[]
  equipes: string[]
  agences: string[]
  statuts: string[]
}

const EMPTY_FACETS: H3aFacets = {
  ids: [], commercials: [], auteurs: [], bus: [], regions: [], equipes: [], agences: [], statuts: [],
}

export interface H3aDetailFilters {
  fId?: number[]
  fCommercial?: string[]
  fAuteur?: string[]
  fBu?: string[]
  fRegion?: string[]
  fEquipe?: string[]
  fAgence?: string[]
  fStatut?: string[]
  fHasOrder?: 'yes' | 'no'
  fRdvFrom?: string
  fRdvTo?: string
  sortBy?: H3aSortBy | null
  sortDir?: 'asc' | 'desc'
}

export interface H3aStatsFilters extends H3aDetailFilters {
  year: number
  bu: H3aBu
  dateField: H3aDateField
  matchKey: H3aMatchKey
  windowMonths: number
  commercialId?: number | null
  refresh?: boolean
}

export type H3aExportType = 'detail' | 'monthly'
export type H3aExportFormat = 'xlsx' | 'csv'

export const useH3aStatsStore = defineStore('h3aStats', () => {
  const suiteStore = useSuiteStore()

  const detail = ref<H3aDetailRow[]>([])
  const monthly = ref<H3aMonthlyRow[]>([])
  const commercials = ref<CommercialOption[]>([])
  const generatedAt = ref<string | null>(null)
  const productSplit = ref(true)
  const orderBased = ref(false)
  const labels = ref<H3aMonthlyLabels>({ ...RDV_LABELS })
  const facets = ref<H3aFacets>({ ...EMPTY_FACETS })
  const notes = ref<string[]>([])
  const isLoading = ref(false)
  const isExporting = ref(false)
  const isSavingSettings = ref(false)

  function buildParams(filters: H3aStatsFilters): URLSearchParams {
    const q = new URLSearchParams()
    q.set('year', String(filters.year))
    q.set('bu', filters.bu)
    q.set('dateField', filters.dateField)
    q.set('matchKey', filters.matchKey)
    q.set('windowMonths', String(filters.windowMonths))
    if (filters.commercialId) q.set('commercialId', String(filters.commercialId))
    if (filters.refresh) q.set('refresh', '1')
    // Filtres / tri du détail (params répétés pour les listes)
    filters.fId?.forEach((v) => q.append('fId', String(v)))
    filters.fCommercial?.forEach((v) => q.append('fCommercial', v))
    filters.fAuteur?.forEach((v) => q.append('fAuteur', v))
    filters.fBu?.forEach((v) => q.append('fBu', v))
    filters.fRegion?.forEach((v) => q.append('fRegion', v))
    filters.fEquipe?.forEach((v) => q.append('fEquipe', v))
    filters.fAgence?.forEach((v) => q.append('fAgence', v))
    filters.fStatut?.forEach((v) => q.append('fStatut', v))
    if (filters.fHasOrder) q.set('fHasOrder', filters.fHasOrder)
    if (filters.fRdvFrom) q.set('fRdvFrom', filters.fRdvFrom)
    if (filters.fRdvTo) q.set('fRdvTo', filters.fRdvTo)
    if (filters.sortBy) q.set('sortBy', filters.sortBy)
    if (filters.sortDir) q.set('sortDir', filters.sortDir)
    return q
  }

  async function fetchCommercials(year: number, dateField: H3aDateField, bu: H3aBu) {
    const res = await suiteStore.suiteFetch(
      `h3a-stats/commercials?year=${year}&dateField=${dateField}&bu=${bu}`,
      { method: 'GET', credentials: 'include' },
    )
    if (res && typeof res !== 'boolean' && res.ok) {
      commercials.value = (await res.json()) ?? []
    }
  }

  async function fetchData(filters: H3aStatsFilters) {
    isLoading.value = true
    try {
      const res = await suiteStore.suiteFetch(`h3a-stats/data?${buildParams(filters)}`, {
        method: 'GET',
        credentials: 'include',
      })
      if (res && typeof res !== 'boolean' && res.ok) {
        const json: H3aStatsResult = await res.json()
        detail.value = json.detail ?? []
        monthly.value = json.monthly ?? []
        generatedAt.value = json.generatedAt ?? null
        productSplit.value = json.productSplit ?? true
        orderBased.value = json.orderBased ?? false
        labels.value = json.labels ?? { ...RDV_LABELS }
        facets.value = json.facets ?? { ...EMPTY_FACETS }
        notes.value = json.notes ?? []
      }
    } finally {
      isLoading.value = false
    }
  }

  async function exportFile(filters: H3aStatsFilters, type: H3aExportType, format: H3aExportFormat) {
    isExporting.value = true
    try {
      const q = buildParams(filters)
      q.set('type', type)
      q.set('format', format)
      const res = await suiteStore.suiteFetch(`h3a-stats/export?${q}`, {
        method: 'GET',
        credentials: 'include',
      })
      if (!res || typeof res === 'boolean' || !res.ok) throw new Error("Échec de l'export")

      const blob = await res.blob()
      const filename =
        res.headers.get('Content-Disposition')?.match(/filename="?([^";]+)"?/)?.[1] ??
        `h3a-stats-${type}-${filters.year}.${format}`
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } finally {
      isExporting.value = false
    }
  }

  async function fetchSettings(bu: H3aBu, year: number): Promise<H3aMonthlySetting[]> {
    const res = await suiteStore.suiteFetch(`h3a-stats/settings?bu=${bu}&year=${year}`, {
      method: 'GET',
      credentials: 'include',
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      return (await res.json()) ?? []
    }
    return []
  }

  async function saveSettings(bu: H3aBu, year: number, items: H3aMonthlySetting[]): Promise<boolean> {
    isSavingSettings.value = true
    try {
      const res = await suiteStore.suiteFetch('h3a-stats/settings', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bu, year, items }),
      })
      return !!res && typeof res !== 'boolean' && res.ok
    } finally {
      isSavingSettings.value = false
    }
  }

  return {
    detail,
    monthly,
    commercials,
    generatedAt,
    productSplit,
    orderBased,
    labels,
    facets,
    notes,
    isLoading,
    isExporting,
    isSavingSettings,
    fetchCommercials,
    fetchData,
    exportFile,
    fetchSettings,
    saveSettings,
  }
})
